import { useState } from 'react';
import { Field } from './Field.js';
import { checkEmail, type EmailCheck } from './emailValidation.js';

const NO_CHECK: EmailCheck = { error: null, suggestion: null };

/**
 * Email input with early (on-blur) validation, mirroring DisplayNameField.
 * When the user leaves the field, a malformed address is named specifically
 * right here instead of failing later at signup. Each step's `validate` runs
 * the same `checkEmail` on "Next", so a bad address blocks progress even if the
 * field was never blurred.
 *
 * The `error` prop is the wizard's error (sync 'Required' or the on-Next check)
 * and takes precedence over this field's own on-blur result. The suggestion is
 * advisory: it never blocks, and it hides whenever an error is showing.
 */
export function EmailField({ label, value, onChange, error, autoComplete }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  autoComplete?: string;
}) {
  const [blurCheck, setBlurCheck] = useState<EmailCheck>(NO_CHECK);
  const shownError = error ?? blurCheck.error ?? undefined;
  const suggestion = shownError ? null : blurCheck.suggestion;

  return (
    <div className="flex flex-col gap-2">
      <Field label={label} error={shownError}>
        <input
          className="input"
          type="email"
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => {
            setBlurCheck(NO_CHECK); // clear stale result while they edit; re-checks on next blur / Next
            onChange(e.target.value);
          }}
          onBlur={() => setBlurCheck(checkEmail(value))}
        />
      </Field>
      {suggestion ? (
        <button
          type="button"
          className="self-start text-[0.8rem] text-ink-600 dark:text-ink-300 hover:underline"
          onClick={() => { onChange(suggestion); setBlurCheck(NO_CHECK); }}
        >
          Did you mean <span className="font-medium">{suggestion}</span>?
        </button>
      ) : null}
    </div>
  );
}
